import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, differenceInDays, parseISO } from "date-fns";
import {
  X,
  Loader2,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Clock,
  Image,
  Film,
} from "lucide-react";

interface ProgressPhoto {
  id: string;
  storage_path: string;
  photo_type: string;
  photo_date: string;
  caption: string | null;
}

interface PhotoTransformationTimelineProps {
  photos: ProgressPhoto[];
  photoType?: string;
  autoPlay?: boolean;
  interval?: number;
  onClose?: () => void;
}

const photoTypeLabels: Record<string, string> = {
  front: "Front",
  side: "Side",
  back: "Back",
  other: "Other",
};

export default function PhotoTransformationTimeline({
  photos,
  photoType,
  autoPlay = false,
  interval = 800,
  onClose,
}: PhotoTransformationTimelineProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [playbackSpeed, setPlaybackSpeed] = useState(interval);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageCache, setImageCache] = useState<Record<string, string>>({});
  const playIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Filter photos by type if specified, then sort by date
  const filteredPhotos = photos
    .filter((photo) => !photoType || photo.photo_type === photoType)
    .sort((a, b) => new Date(a.photo_date).getTime() - new Date(b.photo_date).getTime());

  const loadImageUrl = async (storagePath: string): Promise<string | null> => {
    // Check cache first
    if (imageCache[storagePath]) {
      return imageCache[storagePath];
    }

    try {
      const { data, error } = await supabase.storage
        .from("progress-photos")
        .createSignedUrl(storagePath, 3600);

      if (error) throw error;

      // Update cache
      setImageCache((prev) => ({ ...prev, [storagePath]: data.signedUrl }));
      return data.signedUrl;
    } catch (err) {
      console.error("Error loading image:", err);
      return null;
    }
  };

  // Preload all images for smooth animation
  const preloadImages = useCallback(async () => {
    setLoading(true);
    const urls: Record<string, string> = {};

    await Promise.all(
      filteredPhotos.map(async (photo) => {
        const url = await loadImageUrl(photo.storage_path);
        if (url) {
          urls[photo.storage_path] = url;
          // Preload image into browser cache
          const img = new window.Image();
          img.src = url;
        }
      })
    );

    setImageCache((prev) => ({ ...prev, ...urls }));
    setLoading(false);
  }, [filteredPhotos.length]);

  useEffect(() => {
    preloadImages();
  }, [photos]);

  // Update current image when index changes
  useEffect(() => {
    if (filteredPhotos[currentIndex]) {
      const url = imageCache[filteredPhotos[currentIndex].storage_path];
      setImageUrl(url || null);
    }
  }, [currentIndex, imageCache, filteredPhotos]);

  // Playback interval
  useEffect(() => {
    if (isPlaying && filteredPhotos.length > 1) {
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= filteredPhotos.length - 1) {
            return 0; // Loop back to start
          }
          return prev + 1;
        });
      }, playbackSpeed);
    }

    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, filteredPhotos.length]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    setIsPlaying(false);
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setIsPlaying(false);
    setCurrentIndex((prev) => Math.min(filteredPhotos.length - 1, prev + 1));
  };

  const handleSliderChange = ([value]: number[]) => {
    setIsPlaying(false);
    setCurrentIndex(value);
  };

  const currentPhoto = filteredPhotos[currentIndex];
  const firstPhoto = filteredPhotos[0];
  const lastPhoto = filteredPhotos[filteredPhotos.length - 1];

  if (filteredPhotos.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Image className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            No photos available for timeline
          </p>
        </CardContent>
      </Card>
    );
  }

  if (filteredPhotos.length === 1) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Need at least 2 photos to create a transformation timeline
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalDays = firstPhoto && lastPhoto
    ? differenceInDays(parseISO(lastPhoto.photo_date), parseISO(firstPhoto.photo_date))
    : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Film className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Transformation Timeline</CardTitle>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main photo display */}
        <div className="relative aspect-[3/4] bg-muted rounded-lg overflow-hidden">
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Preloading images...</span>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={`Progress photo from ${currentPhoto?.photo_date}`}
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Date overlay */}
          {currentPhoto && (
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <Badge variant="secondary" className="bg-foreground/70 text-background border-0">
                {format(parseISO(currentPhoto.photo_date), "MMM d, yyyy")}
              </Badge>
              <Badge variant="secondary" className="bg-foreground/70 text-background border-0">
                {currentIndex + 1} / {filteredPhotos.length}
              </Badge>
            </div>
          )}

          {/* Playing indicator */}
          {isPlaying && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-destructive animate-pulse gap-1">
                <div className="h-2 w-2 rounded-full bg-foreground" />
                Playing
              </Badge>
            </div>
          )}
        </div>

        {/* Progress slider */}
        <div className="space-y-2">
          <Slider
            value={[currentIndex]}
            onValueChange={handleSliderChange}
            min={0}
            max={filteredPhotos.length - 1}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{firstPhoto && format(parseISO(firstPhoto.photo_date), "MMM d, yyyy")}</span>
            <span>{lastPhoto && format(parseISO(lastPhoto.photo_date), "MMM d, yyyy")}</span>
          </div>
        </div>

        {/* Playback controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            disabled={currentIndex === 0 && !isPlaying}
          >
            <SkipBack className="h-4 w-4" />
          </Button>
          <Button
            variant="default"
            size="icon"
            className="h-12 w-12"
            onClick={handlePlayPause}
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5 ml-0.5" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            disabled={currentIndex === filteredPhotos.length - 1 && !isPlaying}
          >
            <SkipForward className="h-4 w-4" />
          </Button>
        </div>

        {/* Speed control */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm">Speed</Label>
          </div>
          <Select
            value={String(playbackSpeed)}
            onValueChange={(v) => setPlaybackSpeed(Number(v))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1500">Slow (1.5s)</SelectItem>
              <SelectItem value="800">Normal (0.8s)</SelectItem>
              <SelectItem value="500">Fast (0.5s)</SelectItem>
              <SelectItem value="250">Very Fast (0.25s)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-2 border-t text-center">
          <div>
            <p className="text-2xl font-bold">{filteredPhotos.length}</p>
            <p className="text-xs text-muted-foreground">Photos</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{totalDays}</p>
            <p className="text-xs text-muted-foreground">Days</p>
          </div>
          <div>
            <p className="text-2xl font-bold capitalize">
              {currentPhoto ? photoTypeLabels[currentPhoto.photo_type] || currentPhoto.photo_type : "-"}
            </p>
            <p className="text-xs text-muted-foreground">View</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
